import React, { useState, useMemo, useEffect, useCallback } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { ChevronUp, ChevronDown, Trash2, Plus, Download, Upload, Shield, AlertCircle } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import { GoLiveChecklistItem } from "../../../../shared/types"; // Assume shared type or define inline

interface Props {
  goLiveSnapshot?: any;
  generatedAtISO?: string;
  onRefresh?: () => void;
  userRole?: string;
}

interface Item {
  id?: number;
  category: "RBAC" | "Auth" | "DB" | "Backups" | "Logs" | "Build/Versioning" | "Final Smoke Test";
  title: string;
  details?: string | null;
  status: 'not_started' | 'in_progress' | 'blocked' | 'done';
  owner?: string | null;
  notes?: string | null;
  sortOrder: number;
}

type GroupedItems = Record<string, Item[]>;

const STATUS_OPTIONS = [
  { value: 'not_started' as const, label: 'Not Started' },
  { value: 'in_progress' as const, label: 'In Progress' },
  { value: 'blocked' as const, label: 'Blocked' },
  { value: 'done' as const, label: 'Done' },
];

const CATEGORIES = [
  'RBAC',
  'Auth',
  'DB',
  'Backups',
  'Logs',
  'Build/Versioning',
  'Final Smoke Test'
];

export function GoLiveChecklistPanel({ goLiveSnapshot, generatedAtISO, onRefresh, userRole }: Props) {
  const queryClient = trpc.useUtils();

  const [sending, setSending] = useState(false);

  const [testAlertStatus, setTestAlertStatus] = useState<"idle" | "sending" | "sent" | "failed">("idle");

  const [testAlertMsg, setTestAlertMsg] = useState<string>("");

  const [testAlertAtISO, setTestAlertAtISO] = useState<string>("");

  const handleTestAlert = async () => {
    if (sending) return;
    setTestAlertStatus("sending");
    setSending(true);
    try {
      const res = await fetch('/api/admin/alerts/test', { method: 'POST' });
      const data = await res.json();
      const now = new Date().toISOString();
      if (data.ok && data.sent) {
        setTestAlertStatus("sent");
        setTestAlertAtISO(now);
        toast.success('Test alert sent successfully');
      } else if (data.reason === 'not_configured') {
        setTestAlertStatus("failed");
        setTestAlertMsg("ALERT_WEBHOOK_URL not set");
        setTestAlertAtISO(now);
        toast.info('ALERT_WEBHOOK_URL not set');
      } else {
        setTestAlertStatus("failed");
        setTestAlertMsg(data.reason || 'Unknown error');
        setTestAlertAtISO(now);
        toast.error('Failed to send test alert', { description: data.reason || 'Unknown error' });
      }
    } catch (err) {
      const now = new Date().toISOString();
      setTestAlertStatus("failed");
      setTestAlertMsg("Request failed");
      setTestAlertAtISO(now);
      toast.error('Request failed');
    } finally {
      setSending(false);
    }
  };

  // UI-only guard: only admins can edit the checklist
  const canEdit = userRole === 'admin';

  const query = trpc.system.getChecklist.useQuery(undefined, {
    staleTime: 30 * 1000,
  });

  const metaQuery = trpc.system.getChecklistMeta.useQuery();

  const saveMutation = trpc.system.saveChecklist.useMutation({
    onSuccess: () => {
      toast.success('Checklist saved successfully');
      queryClient.system.getChecklist.invalidate();
      queryClient.system.getChecklistMeta.invalidate();
      setDirty(false);
    },
    onError: (err) => toast.error('Save failed', { description: err.message }),
  });


  const resetMutation = trpc.system.resetChecklistToTemplate.useMutation({
    onSuccess: () => {
      toast.success('Checklist reset to template');
      queryClient.system.getChecklist.invalidate();
      queryClient.system.getChecklistMeta.invalidate();
      setDirty(false);
    },
    onError: (err) => toast.error('Reset failed', { description: err.message }),
  });


  const importMutation = trpc.system.importChecklist.useMutation({
    onSuccess: () => {
      toast.success('Checklist imported');
      query.refetch();
      setDirty(false);
    },
    onError: (err) => toast.error('Import failed', { description: err.message }),
  });

  const [localItems, setLocalItems] = useState<Item[]>([]);
  const [dirty, setDirty] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);

  useEffect(() => {
    if (query.data?.rawItems) {
      setLocalItems(query.data.rawItems as Item[]);
      setDirty(false);
    }
  }, [query.data]);

  const groupedItems = useMemo((): GroupedItems => {
    const grouped: GroupedItems = {};
    for (const item of localItems) {
      if (!grouped[item.category]) {
        grouped[item.category] = [];
      }
      grouped[item.category].push(item);
    }
    CATEGORIES.forEach(cat => {
      if (grouped[cat]) {
        grouped[cat].sort((a, b) => a.sortOrder - b.sortOrder);
      }
    });
    return grouped;
  }, [localItems]);

  // Compute completion metrics
  const completionStats = useMemo(() => {
    const total = localItems.length;
    const done = localItems.filter(item => item.status === 'done').length;
    const percent = total > 0 ? Math.round((done / total) * 100) : 0;
    
    // Determine badge variant based on completion
    let variant: 'destructive' | 'default' | 'secondary' = 'destructive';
    if (percent >= 90) {
      variant = 'default'; // green-ish
    } else if (percent >= 50) {
      variant = 'secondary'; // yellow-ish
    }
    
    return { total, done, percent, variant };
  }, [localItems]);

  const updateItem = useCallback((id: number, updates: Partial<Omit<Item, 'id' | 'sortOrder'>>) => {
    setLocalItems(prev => prev.map(item => 
      item.id === id ? { ...item, ...updates } : item
    ));
    setDirty(true);
  }, []);

  const deleteItem = useCallback((id: number) => {
    setLocalItems(prev => prev.filter(item => item.id !== id));
    setDirty(true);
  }, []);

  const addItem = useCallback((category: string) => {
    const maxSort = Math.max(0, ...localItems.filter(item => item.category === category).map(item => item.sortOrder));
    const newItem: Item = {
      category: category as Item['category'],
      title: `New item in ${category}`,
      status: 'not_started',
      sortOrder: maxSort + 1,
    };
    setLocalItems(prev => [...prev, newItem]);
    setDirty(true);
  }, [localItems]);

  const moveItem = useCallback((id: number, direction: 'up' | 'down') => {
    setLocalItems(prev => {
      const index = prev.findIndex(item => item.id === id);
      if (index === -1) return prev;
      const newItems = [...prev];
      const swapIndex = direction === 'up' ? index - 1 : index + 1;
      if (swapIndex < 0 || swapIndex >= newItems.length || newItems[index].category !== newItems[swapIndex].category) return prev;
      [newItems[index], newItems[swapIndex]] = [newItems[swapIndex], newItems[index]];
      // Reassign sortOrder per category
      const catItemsMap = new Map<string, Item[]>();
      newItems.forEach(item => {
        if (!catItemsMap.has(item.category)) catItemsMap.set(item.category, []);
        catItemsMap.get(item.category)!.push(item);
      });
      catItemsMap.forEach((catItems, cat) => {
        catItems.sort((a, b) => a.sortOrder - b.sortOrder);
        catItems.forEach((item, idx) => {
          const origIndex = prev.findIndex(p => p.id === item.id);
          newItems[origIndex] = { ...newItems[origIndex], sortOrder: idx };
        });
      });
      return newItems;
    });
    setDirty(true);
  }, []);

  const handleSave = useCallback(() => {
    if (!dirty || localItems.length === 0) return;
    // Reassign sequential sortOrder
    const sortedItems = [...localItems].sort((a, b) => a.sortOrder - b.sortOrder);
    const catGroups = new Map<string, Item[]>();
    sortedItems.forEach(item => {
      if (!catGroups.has(item.category)) catGroups.set(item.category, []);
      catGroups.get(item.category)!.push(item);
    });
    const itemsToSave: any[] = [];
    catGroups.forEach(catItems => {
      catItems.sort((a, b) => a.sortOrder - b.sortOrder);
      catItems.forEach((item, idx) => {
        itemsToSave.push({ 
          ...item, 
          sortOrder: idx,
          details: item.details === null ? undefined : item.details,
          owner: item.owner === null ? undefined : item.owner,
          notes: item.notes === null ? undefined : item.notes,
        });
      });
    });
    saveMutation.mutate(itemsToSave);
  }, [localItems, dirty, saveMutation]);

  const handleReset = useCallback(() => {
    if (!confirm('This will reset the checklist to the canonical template. Continue?')) return;
    resetMutation.mutate();
  }, [resetMutation]);

  const handleExport = useCallback(() => {
    const json = JSON.stringify(localItems, null, 2);
    navigator.clipboard.writeText(json).then(() => {
      toast.success('Checklist JSON copied to clipboard');
    }).catch(() => toast.error('Copy failed'));
  }, [localItems]);

  const handleImportFile = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const json = e.target?.result as string;
        const items: Item[] = JSON.parse(json);
        // Validate
        if (!Array.isArray(items)) throw new Error('Not an array');
        items.forEach((item, idx) => {
          if (!CATEGORIES.includes(item.category as string)) throw new Error(`Invalid category "${item.category}" at index ${idx}`);
          if (!STATUS_OPTIONS.some(s => s.value === item.status)) throw new Error(`Invalid status "${item.status}" at index ${idx}`);
          if (!item.title || typeof item.title !== 'string') throw new Error(`Invalid title at index ${idx}`);
        });
        setLocalItems(items);
        setDirty(true);
        toast.success('Checklist imported locally. Click "Update Checklist" to save to database.');
      } catch (err: any) {
        toast.error('Import failed', { description: err.message });
      }
    };
    reader.readAsText(file);
  }, []);

  if (query.isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Go-Live Checklist</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (query.error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Go-Live Checklist</CardTitle>
        </CardHeader>
        <CardContent className="text-destructive">
          Failed to load checklist: {query.error.message}
          <Button onClick={() => query.refetch()} className="mt-2">
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="overflow-hidden">
      <CardHeader className="flex flex-col gap-3 sm:gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 flex-shrink">
          <CardTitle className="flex items-center gap-2 flex-wrap">
            Go-Live Checklist
            {completionStats.total > 0 && (
              <Badge variant={completionStats.variant} className="text-sm font-normal">
                {completionStats.percent}% complete ({completionStats.done}/{completionStats.total})
              </Badge>
            )}
          </CardTitle>
          <CardDescription className="break-words">
            Track deployment readiness. {metaQuery.data ? `Last updated ${new Date(metaQuery.data.lastUpdatedAt).toLocaleString()} by ${metaQuery.data.updatedByDisplay} (${metaQuery.data.lastAction})` : 'No updates yet'}
          </CardDescription>
        </div>
        <div className="flex flex-wrap gap-2 justify-start w-full sm:w-auto sm:flex-shrink-0 sm:min-w-0">
          {dirty && <Badge variant="destructive" className="flex-shrink-0">Unsaved Changes</Badge>}
          {!canEdit && <Badge variant="secondary" className="flex-shrink-0">Read-Only (Admin only)</Badge>}
          <Button onClick={handleSave} disabled={!canEdit || !dirty || saveMutation.isPending} size="sm" className="flex-shrink-0" title={!canEdit ? "Admin only" : ""}>
            {saveMutation.isPending ? 'Saving...' : 'Update Checklist'}
          </Button>
          <Button onClick={handleReset} variant="outline" size="sm" disabled={!canEdit || resetMutation.isPending} className="flex-shrink-0 whitespace-nowrap" title={!canEdit ? "Admin only" : ""}>
            {resetMutation.isPending ? 'Resetting...' : 'Reset to Template'}
          </Button>
          <Button onClick={handleExport} variant="outline" size="sm" className="flex-shrink-0 whitespace-nowrap">
            <Download className="h-4 w-4 mr-1" />
            Export JSON
          </Button>
          <Button asChild variant="outline" size="sm" disabled={!canEdit} className="flex-shrink-0 whitespace-nowrap" title={!canEdit ? "Admin only" : ""}>
            <label className={`inline-flex items-center ${canEdit ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`}>
              <input type="file" accept=".json" onChange={handleImportFile} className="sr-only" disabled={!canEdit} />
              <Upload className="h-4 w-4 mr-1" />
              Import JSON
            </label>
          </Button>
        </div>
      </CardHeader>
  <CardContent>
    {completionStats.percent >= 90 && completionStats.total > 0 && (
      <div className="mb-6 p-6 bg-green-50 border border-green-200 rounded-xl dark:bg-green-950 dark:border-green-800">
        <div className="flex items-center gap-3">
          <div className="flex-shrink-0">
            <div className="w-12 h-12 rounded-full bg-green-500 dark:bg-green-600 flex items-center justify-center">
              <span className="text-2xl font-bold text-white">✅</span>
            </div>
          </div>
          <div>
            <h3 className="text-2xl font-bold text-green-800 dark:text-green-200 mb-1">Ready for Go-Live</h3>
            <p className="text-lg text-green-700 dark:text-green-300">Checklist is {completionStats.percent}% complete ({completionStats.done}/{completionStats.total})</p>
          </div>
        </div>
      </div>
    )}
    {goLiveSnapshot && (
      <div className="mb-6 p-4 border rounded-lg bg-muted/50">
        <h4 className="font-semibold mb-3 flex items-center gap-2">
          <Shield className="h-4 w-4" />
          System Status Snapshot
          <span className="text-xs text-muted-foreground ml-auto">
            {new Date(goLiveSnapshot.generatedAtISO).toLocaleString()}
          </span>
        </h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
          <Badge variant={goLiveSnapshot.database?.reachable ? "default" : "destructive"}>
            Database: {goLiveSnapshot.database?.reachable ? "✅ Reachable" : "❌ Unreachable"}
          </Badge>
          <Badge variant={goLiveSnapshot.security?.jwtSecretSet ? "default" : "destructive"}>
            JWT Secret: {goLiveSnapshot.security?.jwtSecretSet ? "✅ Configured" : "❌ Missing"}
          </Badge>
          <Badge variant={goLiveSnapshot.storage?.uploadsDirExists && goLiveSnapshot.storage?.uploadsWritable ? "default" : "secondary"}>
            Storage: {goLiveSnapshot.storage?.uploadsDirExists ? (goLiveSnapshot.storage.uploadsWritable ? "✅ Ready" : "⚠️ Partial") : "❌ Missing"}
          </Badge>
          <Badge variant={goLiveSnapshot.backups?.backupCount > 0 ? "default" : "secondary"}>
            Backups: {goLiveSnapshot.backups?.backupCount > 0 ? (goLiveSnapshot.backups.lastSuccess ? `✅ Set (${goLiveSnapshot.backups.backupCount})` : `⚠️ Stale (${goLiveSnapshot.backups.backupCount})`) : "⚠️ Not set"}
          </Badge>
          <Badge variant={goLiveSnapshot.app?.nodeEnv === "production" ? "default" : "secondary"}>
            Environment: {goLiveSnapshot.app?.nodeEnv || "unknown"}
          </Badge>
          <Badge variant={goLiveSnapshot.alerts?.webhookConfigured ? "default" : "secondary"}>
            Alerts: {goLiveSnapshot.alerts?.webhookConfigured ? "✅ Active" : "⚠️ Disabled"}
          </Badge>
        </div>
        {userRole === 'admin' && (
          <div className="col-span-full flex gap-2 mt-2 items-center justify-end">
            <Button
              size="sm"
              variant="outline"
              onClick={handleTestAlert}
              disabled={sending || testAlertStatus === "sending" || !goLiveSnapshot?.alerts?.webhookConfigured}
              className="h-8"
              title={!goLiveSnapshot?.alerts?.webhookConfigured ? "ALERT_WEBHOOK_URL not configured" : "Send test notification to verify webhook"}
            >
              {sending || testAlertStatus === "sending" ? "Sending..." : (
                <>
                  <AlertCircle className="h-4 w-4 mr-1" />
                  Test Alert
                </>
              )}
            </Button>
            {testAlertStatus !== "idle" && (
              <Badge variant={testAlertStatus === "sent" ? "default" : "destructive"} className="text-xs whitespace-nowrap">
                {testAlertStatus === "sent" ? "✅ Sent" : `❌ Failed (${testAlertMsg})`}
                <span className="ml-1 text-xs opacity-75">
                  {new Date(testAlertAtISO).toLocaleTimeString()}
                </span>
              </Badge>
            )}
          </div>
        )}
      </div>
    )}
    <Accordion type="multiple" className="w-full">
          {CATEGORIES.map((category) => {
            const group = groupedItems[category];
            if (!group || group.length === 0) return null;
            const doneCount = group.filter((item) => item.status === 'done').length;
            return (
              <AccordionItem key={category} value={category}>
                <AccordionTrigger>
                  <div className="flex items-center gap-2">
                    <strong>{category}</strong>
                    <Badge variant={doneCount === group.length ? 'default' : 'secondary'}>
                      {doneCount}/{group.length}
                    </Badge>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-3">
                    {group.map((item, idx) => (
                      <div key={item.id || idx} className="border rounded-lg p-4 space-y-3">
                        <div className="flex items-center gap-3">
                          <div className="flex-1">
                            <Label>Title</Label>
                            <Input
                              value={item.title}
                              onChange={(e) => updateItem(item.id!, { title: e.target.value })}
                              disabled={!canEdit}
                            />
                          </div>
                          <div className="w-32">
                            <Label>Status</Label>
                            <Select value={item.status} onValueChange={(value) => updateItem(item.id!, { status: value as any })} disabled={!canEdit}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {STATUS_OPTIONS.map((opt) => (
                                  <SelectItem key={opt.value} value={opt.value}>
                                    {opt.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="w-24">
                            <Label>Owner</Label>
                            <Input
                              value={item.owner || ''}
                              onChange={(e) => updateItem(item.id!, { owner: e.target.value || undefined })}
                              placeholder="Optional"
                              disabled={!canEdit}
                            />
                          </div>
                        </div>
                        {item.details && (
                          <div>
                            <Label>Details (read-only)</Label>
                            <div className="text-sm text-muted-foreground p-3 border rounded">{item.details}</div>
                          </div>
                        )}
                        <div>
                          <Label>Notes</Label>
                          <Textarea
                            value={item.notes || ''}
                            onChange={(e) => updateItem(item.id!, { notes: e.target.value || undefined })}
                            placeholder="Optional notes..."
                            rows={3}
                            disabled={!canEdit}
                          />
                        </div>
                        <div className="flex items-center gap-2 pt-2">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => moveItem(item.id!, 'up')}
                            disabled={!canEdit || idx === 0}
                            title={!canEdit ? "Admin only" : ""}
                          >
                            <ChevronUp className="h-4 w-4" />
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => moveItem(item.id!, 'down')}
                            disabled={!canEdit || idx === group.length - 1}
                            title={!canEdit ? "Admin only" : ""}
                          >
                            <ChevronDown className="h-4 w-4" />
                          </Button>
                          <Button
                            type="button"
                            variant="destructive"
                            size="sm"
                            onClick={() => {
                              if (confirm('Delete this item?')) deleteItem(item.id!);
                            }}
                            disabled={!canEdit}
                            title={!canEdit ? "Admin only" : ""}
                          >
                            <Trash2 className="h-4 w-4 mr-1" />
                            Delete
                          </Button>
                        </div>
                      </div>
                    ))}
                    <Button
                      variant="ghost"
                      className="w-full justify-start"
                      onClick={() => addItem(category)}
                      disabled={!canEdit}
                      title={!canEdit ? "Admin only" : ""}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add item to {category}
                    </Button>
                  </div>
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      </CardContent>
    </Card>
  );
}
